import NearestNeighborProcessor from "./nearest-neighbor-processor";
import InverseDistanceWeightProcessor from "./inverse-distance-weight-processor";

export default class AlgorithmProcessor {

  /**
   *
   * @param algorithm
   * @param siteDistanceData
   */
  constructor(algorithm, siteDistanceData) {
    this._numberOfNeighbours = 0;
    this._processor = null;
    this._siteDistanceData = siteDistanceData || [];

    if (algorithm) {
      this.algorithm = algorithm;
    }
  }

  /**
   * Create a processor depending on the algorithm
   * @param algorithm
   */
  set algorithm(algorithm) {
    switch (algorithm) {
      case 'nearest-neighbour':
        this._numberOfNeighbours = 1;
        this._processor = new NearestNeighborProcessor();
        break;
      case 'inverseDistWeight-p1-n4':
        this._numberOfNeighbours = 4;
        this._processor = new InverseDistanceWeightProcessor(1);
        break;
      case 'inverseDistWeight-p1-n6':
        this._numberOfNeighbours = 6;
        this._processor = new InverseDistanceWeightProcessor(1);
        break;
      case 'inverseDistWeight-p2-n4':
        this._numberOfNeighbours = 4;
        this._processor = new InverseDistanceWeightProcessor(2);
        break;
      case 'inverseDistWeight-p2-n6':
        this._numberOfNeighbours = 6;
        this._processor = new InverseDistanceWeightProcessor(2);
        break;
      default:
        throw "Invalid algorithm";
    }
  }

  /**
   * Setter for the {@link #siteDistanceData}.
   */
  set siteDistanceData(siteDistanceData) {
    this._siteDistanceData = siteDistanceData;
  }

  /**
   * @param referenceData
   * @param selectedSiteIds
   * return modelData
   */
  process(referenceData, selectedSiteIds) {
    if (this._processor === null) {
      throw "No valid algorithm chosen";
    }

    // Build a subset of reference sites ('lookupData') that contain only those sites that have
    // been selected ('selectedSiteIds').
    let lookupData = {};
    for (let siteId of selectedSiteIds) {
      let site = referenceData[siteId];
      if (site) {
        lookupData[siteId] = site;
      }
    }

    // Loop through each site, performing the resampling on each site.
    let result = {};
    Object.keys(referenceData).forEach(siteId => {
      const nearestNeighbours = this._getNearestNeighbours(siteId, referenceData[siteId]['index'], lookupData);
      let rowModelData = this._processor.process(referenceData, nearestNeighbours, siteId);
      if (rowModelData) {
        let site = referenceData[siteId];
        result[siteId] = {
          lat: site.lat,
          lon: site.lon,
          data: rowModelData
        };
      }
    });
    return result;
  }

  /**
   * For a specified site ('siteId'), identify the nearest neighbours from the list of possible
   * candidates ('lookupSites').
   *
   * @param siteId the site for which we are identifying the nearest neighbours.
   * @param siteIndex
   * @param lookupSites the list of sites from which the nearest neighbours can be selected.
   * @return {Array}
   * @private
   */
  _getNearestNeighbours(siteId, siteIndex, lookupSites) {

    // Initialise an array that we will then populate with the nearest neighbours of siteId.
    let nearestNeighbours = [];
    for (let i = 0; i < this._numberOfNeighbours; i++) {
      nearestNeighbours[i] = {
        "squaredDistance": null,
        "siteId": null
      }
    }

    // Loop through each of the look up sites, determining it's squared distance to the site of
    // interest. If the lookup site is closer (squaredDistance is smaller) than any of the
    // previously identified near neighbours, insert this lookup site into the list and push the
    // further sites down the list, possibly/probably dropping them off as near neighbours.
    Object.keys(lookupSites).forEach(lookupSiteId => {

      let squaredDistance = siteId === lookupSiteId ? 0 :
        this._siteDistanceData[siteIndex][lookupSites[lookupSiteId]['index']] || this._siteDistanceData[lookupSites[lookupSiteId]['index']][siteIndex];

      for (let i = 0; i < this._numberOfNeighbours; i++) {
        if (nearestNeighbours[i].squaredDistance === null || squaredDistance <= nearestNeighbours[i].squaredDistance) {
          for (let j = this._numberOfNeighbours - 1; j > i; j--) {
            nearestNeighbours[j].squaredDistance = nearestNeighbours[j - 1].squaredDistance;
            nearestNeighbours[j].siteId = nearestNeighbours[j - 1].siteId;
          }
          nearestNeighbours[i].squaredDistance = squaredDistance;
          nearestNeighbours[i].siteId = lookupSiteId;
          break;
        }
      }
    });
    return nearestNeighbours;
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/algorithm-processor/algorithm-processor.js